/**
 * PortalInvoiceList — planTEK Wave 1: Stripe portal payment integration.
 *
 * Displays outstanding invoices for EXTERNAL portal users and provides
 * a Stripe Elements payment form for each invoice.
 *
 * Data flow:
 *   1. Mount: fetch GET /api/portal/invoices + GET /api/portal/stripe-config
 *   2. Empty state: "No outstanding invoices" message
 *   3. "Pay Now": load Stripe Elements in-place, show CardElement
 *   4. Confirm: stripe.confirmCardPayment(client_secret) → success/error
 *   5. On success: mark invoice paid locally + show confirmation
 *
 * AC4: renders list / empty state
 * AC5: "Pay Now" → Stripe payment form → confirmation on success
 * AC9: Stripe.js added via package.json (@stripe/stripe-js, @stripe/react-stripe-js)
 */
import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { CreditCard, CheckCircle2, AlertCircle, Loader2, Receipt } from 'lucide-react';
import { api } from '@/lib/api';
import { InvoicePaymentHistory } from '@/components/invoices/InvoicePaymentHistory'; // V172: payment ledger

// ─── Types ───────────────────────────────────────────────────────────────────

interface PortalInvoice {
  id: string;
  invoice_number: string;
  total_amount: number;
  amount_paid: number;
  due_date: string | null;
  invoice_date: string | null;
  status: string;
  project_name: string | null;
  organization_name: string | null;
  /** The version of the invoice the client is shown (R2 portal read); sent back when paying. */
  version?: string | null;
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

function usePortalInvoices() {
  return useQuery({
    queryKey: ['portal', 'invoices'],
    queryFn: () => api.portalPayments.listInvoices(),
    staleTime: 60_000,
  });
}

function useStripeConfig() {
  return useQuery({
    queryKey: ['portal', 'stripe-config'],
    queryFn: () => api.portalPayments.getStripeConfig(),
    staleTime: 10 * 60_000,
  });
}

// ─── Card element options (matches existing portal theme) ────────────────────

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '14px',
      color: 'var(--foreground, #111)',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      '::placeholder': { color: 'var(--muted-foreground, #9ca3af)' },
    },
    invalid: { color: '#ef4444' },
  },
};

// ─── PaymentForm — rendered inside an <Elements> provider per invoice ─────────

interface PaymentFormProps {
  invoice: PortalInvoice;
  clientSecret: string;
  onSuccess: () => void;
  onCancel: () => void;
}

function PaymentForm({ invoice, clientSecret, onSuccess, onCancel }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  // An overpayment is cash held on the invoice, not a negative balance (SPEC-invoice-merge-history R4 AC5).
  const outstanding = Math.max(0, invoice.total_amount - (invoice.amount_paid ?? 0));

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!stripe || !elements) return;

      const card = elements.getElement(CardElement);
      if (!card) return;

      setProcessing(true);
      setError(null);

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        { payment_method: { card } },
      );

      if (stripeError) {
        setError(stripeError.message ?? 'Payment failed. Please try again.');
        setProcessing(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        onSuccess();
      } else {
        setError('Payment did not complete. Please try again.');
        setProcessing(false);
      }
    },
    [stripe, elements, clientSecret, onSuccess],
  );

  return (
    <form onSubmit={handleSubmit} className="mt-4 space-y-4">
      <div
        className="rounded-lg border px-4 py-3"
        style={{ borderColor: 'var(--border, #e5e7eb)' }}
      >
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2.5">
          <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={!stripe || processing}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
          style={{
            backgroundColor: 'var(--primary)',
            color: 'var(--primary-foreground, #fff)',
          }}
        >
          {processing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <CreditCard className="w-4 h-4" />
          )}
          {processing ? 'Processing…' : `Pay ${_formatCurrency(outstanding)}`}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className="px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
          style={{ color: 'var(--muted-foreground, #6b7280)' }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── InvoiceRow — single invoice card with optional payment form ──────────────

interface InvoiceRowProps {
  invoice: PortalInvoice;
  stripePromise: ReturnType<typeof loadStripe> | null;
  onPaid: (invoiceId: string) => void;
}

function InvoiceRow({ invoice, stripePromise, onPaid }: InvoiceRowProps) {
  const [payMode, setPayMode] = useState<'idle' | 'loading' | 'form' | 'success'>('idle');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [payError, setPayError] = useState<string | null>(null);

  // An overpayment is cash held on the invoice, not a negative balance (SPEC-invoice-merge-history R4 AC5).
  const outstanding = Math.max(0, invoice.total_amount - (invoice.amount_paid ?? 0));
  const isOverdue = invoice.due_date
    ? new Date(invoice.due_date + 'T00:00:00') < new Date()
    : false;

  const handlePayNow = useCallback(async () => {
    setPayMode('loading');
    setPayError(null);
    try {
      const result = await api.portalPayments.payInvoice(invoice.id, invoice.version);
      setClientSecret(result.client_secret);
      setPayMode('form');
    } catch (err: any) {
      setPayError(err?.message ?? 'Failed to start payment. Please try again.');
      setPayMode('idle');
    }
  }, [invoice.id, invoice.version]);

  if (payMode === 'success') {
    return (
      <div
        className="rounded-xl border px-5 py-4 flex items-start gap-3"
        style={{
          backgroundColor: 'color-mix(in srgb, #16a34a 8%, var(--background))',
          borderColor: '#16a34a44',
        }}
      >
        <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-green-800">Payment received!</p>
          <p className="text-xs text-green-700 mt-0.5">
            {invoice.invoice_number} — {invoice.project_name}. Your receipt has been sent.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border shadow-sm overflow-hidden"
      style={{
        backgroundColor: 'var(--card, var(--background))',
        borderColor: 'var(--border, #e5e7eb)',
      }}
    >
      <div className="px-5 py-4">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
              {invoice.invoice_number}
            </p>
            {invoice.project_name && (
              <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground, #6b7280)' }}>
                {invoice.project_name}
              </p>
            )}
          </div>
          <div className="text-right shrink-0">
            <p className="text-base font-bold" style={{ color: 'var(--foreground)' }}>
              {_formatCurrency(outstanding)}
            </p>
            {invoice.due_date && (
              <p
                className="text-xs mt-0.5"
                style={{ color: isOverdue ? '#ef4444' : 'var(--muted-foreground, #6b7280)' }}
              >
                {isOverdue ? 'Overdue · ' : 'Due '}
                {new Date(invoice.due_date + 'T00:00:00').toLocaleDateString('en-CA', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </p>
            )}
          </div>
        </div>

        {/* Pay Now button / error */}
        {payMode === 'idle' && (
          <div className="mt-3">
            {payError && (
              <p className="text-xs text-red-600 mb-2">{payError}</p>
            )}
            <button
              onClick={handlePayNow}
              disabled={!stripePromise}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
              style={{
                backgroundColor: 'var(--primary)',
                color: 'var(--primary-foreground, #fff)',
              }}
            >
              <CreditCard className="w-3.5 h-3.5" />
              Pay Now
            </button>
          </div>
        )}

        {payMode === 'loading' && (
          <div className="mt-3 flex items-center gap-2 text-xs" style={{ color: 'var(--muted-foreground, #6b7280)' }}>
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Preparing payment…
          </div>
        )}

        {/* Stripe Elements form */}
        {payMode === 'form' && clientSecret && stripePromise && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentForm
              invoice={invoice}
              clientSecret={clientSecret}
              onSuccess={() => {
                setPayMode('success');
                onPaid(invoice.id);
              }}
              onCancel={() => {
                setPayMode('idle');
                setClientSecret(null);
              }}
            />
          </Elements>
        )}

        {/* V172: Payment history ledger for portal clients */}
        <InvoicePaymentHistory invoiceId={invoice.id} portalMode />
      </div>
    </div>
  );
}

// ─── Public component ─────────────────────────────────────────────────────────

/**
 * PortalInvoiceList — drop-in panel for the external portal.
 *
 * Fetches invoices + stripe config, renders invoice cards with Pay Now buttons.
 * Self-contained: manages its own loading/error/empty states.
 */
export function PortalInvoiceList() {
  const queryClient = useQueryClient();
  const { data: invoicesData, isLoading: loadingInvoices, error: invoicesError } = usePortalInvoices();
  const { data: stripeData, isLoading: loadingStripe } = useStripeConfig();

  // Memoize the stripePromise so it isn't re-created on each render
  const [stripePromise] = useState(() => {
    if (stripeData?.publishable_key) {
      return loadStripe(stripeData.publishable_key);
    }
    return null;
  });

  // Once we have the publishable key, initialise Stripe (handles async config load)
  const [resolvedStripePromise, setResolvedStripePromise] = useState<ReturnType<typeof loadStripe> | null>(stripePromise);

  // Initialise when stripe config loads
  if (stripeData?.publishable_key && !resolvedStripePromise) {
    setResolvedStripePromise(loadStripe(stripeData.publishable_key));
  }

  const handlePaid = useCallback((invoiceId: string) => {
    // Optimistically remove from list after a short delay (webhook will confirm async)
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'invoices'] });
    }, 3000);
  }, [queryClient]);

  const isLoading = loadingInvoices || loadingStripe;
  const invoices: PortalInvoice[] = invoicesData?.invoices ?? [];

  return (
    <div
      className="rounded-xl border shadow-sm overflow-hidden"
      style={{
        backgroundColor: 'var(--card, var(--background))',
        borderColor: 'var(--border, #e5e7eb)',
      }}
    >
      {/* Title bar */}
      <div
        className="flex items-center gap-2 px-5 py-3.5 border-b"
        style={{ borderColor: 'var(--border, #e5e7eb)' }}
      >
        <Receipt className="w-4 h-4" style={{ color: 'var(--muted-foreground, #6b7280)' }} />
        <h2 className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
          Outstanding Invoices
        </h2>
        {invoices.length > 0 && (
          <span
            className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--primary) 12%, transparent)',
              color: 'var(--primary)',
            }}
          >
            {invoices.length}
          </span>
        )}
      </div>

      <div className="px-5 py-4">
        {/* Loading */}
        {isLoading && (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="h-16 rounded-lg animate-pulse"
                style={{ backgroundColor: 'var(--muted, #e5e7eb)' }}
              />
            ))}
          </div>
        )}

        {/* Error */}
        {!isLoading && invoicesError && (
          <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2.5">
            <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">Failed to load invoices. Please refresh.</p>
          </div>
        )}

        {/* AC4: Empty state */}
        {!isLoading && !invoicesError && invoices.length === 0 && (
          <div className="py-6 text-center">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
            <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>
              No outstanding invoices
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground, #6b7280)' }}>
              You're all caught up.
            </p>
          </div>
        )}

        {/* Invoice list */}
        {!isLoading && !invoicesError && invoices.length > 0 && (
          <div className="space-y-3">
            {invoices.map((invoice) => (
              <InvoiceRow
                key={invoice.id}
                invoice={invoice}
                stripePromise={resolvedStripePromise}
                onPaid={handlePaid}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Utilities ────────────────────────────────────────────────────────────────

function _formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
  }).format(amount);
}
